// Вкладка «ИИ»: настройки модели на каждую задачу (скриншот, ссылка, текст,
// магия, цены) + общий переключатель, ключ и лимиты. Список моделей грузится
// один раз на всю панель и раздаётся всем ModelPicker пропсом — иначе было бы
// столько же одинаковых запросов на одну и ту же страницу, сколько задач.
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useI18n, useT } from '../i18n';
import type { Translate } from '../i18n';
import { errorKey } from '../auth';
import type { ApiError } from '../auth';
import {
  fetchAiModels,
  fetchAiSettings,
  saveAiSettings,
  testAiSource,
} from './aiApi';
import type { AiKeyInfo, AiModel, AiOverridesPatch, AiSettings, AiSource, AiTuningField } from './aiApi';
import { ModelPicker } from './ModelPicker';
import { RECOMMENDED_AS_OF, RECOMMENDED_MODELS, RECOMMENDED_TUNING } from './recommendedModels';
import { formatPrice, modelById, modelIssues, REASONING_MIN_TOKENS, SOURCE_NEEDS } from './models';
import type { ModelIssue } from './models';
import styles from './AiSettingsPanel.module.css';

// Порядок карточек, а не порядок MODEL_SOURCES на сервере: сначала три
// источника распознавания, потом задачи со своей моделью — магия и цены.
const SOURCES = ['screenshot', 'url', 'text', 'magic', 'prices'] as const satisfies readonly AiSource[];

/**
 * Задача, которая есть в AiSource, но не попала в SOURCES, не получает ни
 * выбора модели, ни своих maxTokens/temperature/timeoutSec/dailyLimit — то
 * есть настроить её из админки нельзя, и молча: панель просто рисует на одну
 * карточку меньше. Ровно так `prices` и прожил незамеченным от появления на
 * сервере. Дальше это ловит компилятор.
 */
type UnlistedSource = Exclude<AiSource, (typeof SOURCES)[number]>;
const _allSourcesListed: [UnlistedSource] extends [never] ? true : never = true;
void _allSourcesListed;

/**
 * Требования к модели магии. У распознавания задача узкая — вынуть поля из
 * куска текста, — и с ней справляется почти любая модель. Магия планирует
 * маршрут по всей библиотеке сразу, и неудачно выбранная модель проваливает её
 * молча: сервер отбрасывает выдуманные id и пересекающиеся рейсы, и человек
 * видит пустой результат вместо ошибки. Поэтому требования перечислены прямо
 * рядом с выбором.
 */
const MAGIC_REQUIREMENTS = [
  'admin.ai.req.context',
  'admin.ai.req.json',
  'admin.ai.req.reasoning',
  'admin.ai.req.tokens',
  'admin.ai.req.vision',
  'admin.ai.req.temp',
] as const;

/** Границы числовых полей — из SPEC.md, сервер проверяет их же. */
const BOUNDS = {
  maxTokens: { min: 256, max: 8000 },
  temperature: { min: 0, max: 1 },
  timeoutSec: { min: 5, max: 180 },
  dailyLimit: { min: 0, max: 1000 },
  maxImages: { min: 1, max: 10 },
} as const;

type BoundedField = keyof typeof BOUNDS;

/**
 * Поля, которые можно переопределить на задачу. `maxImages` сюда не входит:
 * пачка бывает только у скриншотов, и сервер отвергает такое переопределение
 * как неизвестную настройку.
 */
const TUNING_FIELDS = ['maxTokens', 'temperature', 'timeoutSec', 'dailyLimit'] as const satisfies readonly AiTuningField[];

function fieldLabelKey(field: BoundedField): string {
  switch (field) {
    case 'maxTokens':
      return 'admin.ai.maxTokens';
    case 'temperature':
      return 'admin.ai.temperature';
    case 'timeoutSec':
      return 'admin.ai.timeout';
    case 'dailyLimit':
      return 'admin.ai.limit';
    case 'maxImages':
      return 'admin.ai.maxImages';
  }
}

/**
 * error.message — дословный текст от сервера (например, ответ ИИ-провайдера) —
 * есть не для всех кодов. Когда его нет, коды ошибок у /api/admin/ai/* — те
 * же, что у /api/recognize (см. SPEC.md: unauthorized | no_key | provider |
 * timeout | bad_request | ...), поэтому читаемый текст берём из уже готовых
 * ключей recog.err.*. Отдельного набора под админку в словарях нет — заводить
 * новый ключ на пять языков не в нашей власти (src/i18n/translations.ts
 * трогать нельзя), поэтому переиспользуем ближайший подходящий по смыслу
 * набор. 'network' — из общего auth.err.*.
 */
function apiErrorMessage(t: Translate, error: ApiError): string {
  if (error.message) return error.message;
  if (error.error === 'network') return t('auth.err.network');
  const key = `recog.err.${error.error}`;
  const text = t(key);
  return text === key ? error.error : text;
}

type TestOutcome = { ok: true; ms: number } | { ok: false; message: string };

/**
 * Рекомендация одной строкой: «токены 8000 · температура 0 · таймаут 180 с».
 * Подписи берём те же, что у самих полей, — иначе рекомендация и поле, к
 * которому она относится, назывались бы по-разному.
 */
function formatTuning(t: Translate, source: AiSource): string {
  const rec = RECOMMENDED_TUNING[source];
  return [
    `${t('admin.ai.maxTokens')} ${rec.maxTokens}`,
    `${t('admin.ai.temperature')} ${rec.temperature}`,
    `${t('admin.ai.timeout')} ${rec.timeoutSec}`,
  ].join(' · ');
}

/**
 * Текст претензии к выбранной модели. Формулировки разные не для красоты:
 * первые четыре означают «задача не заработает», а 'reasoning' — «заработает,
 * но подними лимит токенов», и смешивать их в одну фразу нельзя.
 */
function issueMessage(t: Translate, issue: ModelIssue, source: AiSource): string {
  switch (issue) {
    case 'json':
      return t('admin.ai.model.warn.json');
    case 'vision':
      return t('admin.ai.model.warn.vision');
    case 'tools':
      return t('admin.ai.model.warn.tools');
    case 'context':
      return t('admin.ai.model.warn.context', {
        min: `${Math.round(SOURCE_NEEDS[source].minContext / 1000)}K`,
      });
    case 'reasoning':
      return t('admin.ai.model.warn.reasoning', { tokens: REASONING_MIN_TOKENS });
  }
}

export function AiSettingsPanel(): ReactElement {
  const t = useT();
  const { lang } = useI18n();

  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [keyInfo, setKeyInfo] = useState<AiKeyInfo | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [apiKeyInput, setApiKeyInput] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [saved, setSaved] = useState(false);

  const [models, setModels] = useState<AiModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState<ApiError | null>(null);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [testing, setTesting] = useState<Partial<Record<AiSource, boolean>>>({});
  const [testResults, setTestResults] = useState<Partial<Record<AiSource, TestOutcome>>>({});

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const res = await fetchAiSettings();
    setLoading(false);
    if (res.ok) {
      setSettings(res.value.settings);
      setKeyInfo(res.value.key);
      setUpdatedAt(res.value.updatedAt);
      setLoadError(null);
      setDirty(false);
    } else {
      setLoadError(res.error);
    }
  }, []);

  const loadModels = useCallback(async (refresh?: boolean) => {
    setModelsLoading(true);
    const res = await fetchAiModels(refresh);
    setModelsLoading(false);
    if (res.ok) {
      setModels(res.value.models);
      setModelsError(null);
    } else {
      setModelsError(res.error);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
    void loadModels();
  }, [loadSettings, loadModels]);

  function update(patch: Partial<AiSettings>): void {
    setSettings((s) => (s ? { ...s, ...patch } : s));
    setDirty(true);
    setSaved(false);
  }

  function updateModel(source: AiSource, id: string): void {
    setSettings((s) => (s ? { ...s, models: { ...s.models, [source]: id } } : s));
    setDirty(true);
    setSaved(false);
  }

  /**
   * Проставляет рекомендованные настройки задачи в её переопределения (а не в
   * общие поля): рекомендации у задач разные, и запись их в общее значение
   * ломала бы остальные четыре.
   */
  function applyRecommendedTuning(source: AiSource): void {
    const rec = RECOMMENDED_TUNING[source];
    setSettings((s) => {
      if (!s) return s;
      const one = { ...(s.overrides[source] ?? {}), ...rec };
      return { ...s, overrides: { ...s.overrides, [source]: one } };
    });
    setDirty(true);
    setSaved(false);
  }

  /** `undefined` убирает переопределение — задача снова берёт общее значение. */
  function updateOverride(source: AiSource, field: AiTuningField, value: number | undefined): void {
    setSettings((s) => {
      if (!s) return s;
      const one = { ...(s.overrides[source] ?? {}) };
      if (value === undefined) delete one[field];
      else one[field] = value;
      const overrides = { ...s.overrides };
      if (Object.keys(one).length > 0) overrides[source] = one;
      else delete overrides[source];
      return { ...s, overrides };
    });
    setDirty(true);
    setSaved(false);
  }

  // Клиентская подсказка о границах — сервер всё равно проверит сам при
  // сохранении. Готового ключа перевода под «значение вне диапазона» в
  // словарях нет (см. комментарий выше про i18n), а `currencies.error.rate`
  // семантически не подходит — там речь о курсе валюты, а не о токенах или
  // секундах. Текст ошибки поэтому — переведённая подпись поля и сами цифры
  // диапазона, без отдельной фразы на естественном языке.
  const rangeErrors = useMemo(() => {
    const errors: Partial<Record<BoundedField, string>> = {};
    if (!settings) return errors;
    (Object.keys(BOUNDS) as BoundedField[]).forEach((field) => {
      const { min, max } = BOUNDS[field];
      const value = settings[field];
      if (!Number.isFinite(value) || value < min || value > max) {
        errors[field] = `${t(fieldLabelKey(field))}: ${min}–${max}`;
      }
    });
    return errors;
  }, [settings, t]);

  // Те же границы — для переопределений на задачу: сервер проверит их так же.
  const overrideErrors = useMemo(() => {
    const errors: Partial<Record<AiSource, Partial<Record<AiTuningField, string>>>> = {};
    if (!settings) return errors;
    for (const source of SOURCES) {
      const one = settings.overrides[source];
      if (!one) continue;
      for (const field of TUNING_FIELDS) {
        const value = one[field];
        if (value === undefined) continue;
        const { min, max } = BOUNDS[field];
        if (!Number.isFinite(value) || value < min || value > max) {
          errors[source] = { ...errors[source], [field]: `${t(fieldLabelKey(field))}: ${min}–${max}` };
        }
      }
    }
    return errors;
  }, [settings, t]);

  /**
   * Претензии к выбранной модели считаются от действующего для задачи лимита
   * токенов — своего, если он переопределён, иначе общего. Иначе подсказка
   * про рассуждение показывалась бы по общему значению и врала бы тем, кто
   * лимит этой задаче уже поднял.
   */
  const issuesFor = useCallback(
    (source: AiSource): ModelIssue[] => {
      if (!settings) return [];
      const maxTokens = settings.overrides[source]?.maxTokens ?? settings.maxTokens;
      return modelIssues(modelById(models, settings.models[source]), source, maxTokens);
    },
    [settings, models],
  );

  const hasErrors = Object.keys(rangeErrors).length > 0 || Object.keys(overrideErrors).length > 0;
  // Новый ключ — такое же несохранённое изменение, как и всё остальное: без
  // этого поле ключа заполнялось, а «Сохранить» оставалась заблокированной,
  // если больше ничего не трогали.
  const keyDirty = apiKeyInput.trim() !== '';
  const canSave = (dirty || keyDirty) && !hasErrors && !saving && settings !== null;

  async function handleSave(): Promise<void> {
    if (!settings || hasErrors || saving) return;
    setSaving(true);
    setSaveError(null);
    // Переопределения уходят полным набором: очищенное поле должно приехать
    // как null, иначе сервер просто не увидит, что его стёрли, и оставит
    // старое значение — человек сохранил бы одно, а получил другое.
    const overrides: AiOverridesPatch = {};
    for (const source of SOURCES) {
      const one = settings.overrides[source] ?? {};
      overrides[source] = Object.fromEntries(TUNING_FIELDS.map((field) => [field, one[field] ?? null]));
    }
    const patch: {
      settings: Omit<Partial<AiSettings>, 'overrides'> & { overrides?: AiOverridesPatch };
      apiKey?: string | null;
    } = { settings: { ...settings, overrides } };
    if (apiKeyInput.trim()) patch.apiKey = apiKeyInput.trim();
    const res = await saveAiSettings(patch);
    setSaving(false);
    if (res.ok) {
      setSettings(res.value.settings);
      setKeyInfo(res.value.key);
      setUpdatedAt(res.value.updatedAt);
      setApiKeyInput(''); // секрету в состоянии формы жить незачем — стираем сразу после успеха
      setDirty(false);
      setSaved(true);
    } else {
      setSaveError(res.error);
    }
  }

  async function handleClearKey(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    const res = await saveAiSettings({ apiKey: null });
    setSaving(false);
    if (res.ok) {
      setKeyInfo(res.value.key);
      setUpdatedAt(res.value.updatedAt);
    } else {
      setSaveError(res.error);
    }
  }

  async function handleTest(source: AiSource): Promise<void> {
    setTesting((s) => ({ ...s, [source]: true }));
    const res = await testAiSource(source);
    setTesting((s) => ({ ...s, [source]: false }));
    setTestResults((s) => ({
      ...s,
      [source]: res.ok
        ? { ok: true, ms: res.value.ms }
        : { ok: false, message: apiErrorMessage(t, res.error) },
    }));
  }

  const formatDate = useCallback(
    (ms: number | null) =>
      ms ? new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' }).format(ms) : '—',
    [lang],
  );

  if (loading) return <p className={styles.muted}>{t('auth.busy')}</p>;

  if (loadError) {
    return (
      <p className={styles.error} role="alert">
        {t(errorKey(loadError))}
      </p>
    );
  }

  // Сюда попадаем только если запрос ответил успехом, но без тела — состояние
  // неотличимо от «ещё грузится», поэтому и подпись та же, а не «никого нет».
  if (!settings || !keyInfo) return <p className={styles.muted}>{t('auth.busy')}</p>;

  return (
    <div className={styles.panel}>
      {/* Каждый блок — секция во всю ширину: общий выключатель, ключ, затем по
          секции на источник. Настройка у источников разная, и складывать их в
          узкие колонки рядом значит резать самое длинное — строку модели. */}
      <section className={styles.keyBlock}>
        <h3 className={styles.subtitle}>{t('admin.ai.title')}</h3>
        <p className={styles.hint}>{t('admin.ai.hint')}</p>
        <label className={styles.switchRow}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
          <span>{t('admin.ai.enabled')}</span>
        </label>
        <p className={styles.hint}>{t('admin.ai.enabled.hint')}</p>
      </section>

      <section className={styles.keyBlock}>
        <h3 className={styles.subtitle}>{t('admin.ai.key')}</h3>
        <p className={styles.keyStatus}>
          {keyInfo.source === 'env'
            ? t('admin.ai.key.env')
            : keyInfo.present
              ? t('admin.ai.key.present', { tail: keyInfo.tail })
              : t('admin.ai.key.missing')}
        </p>
        <div className={styles.keyRow}>
          <input
            className="input"
            type="password"
            autoComplete="off"
            placeholder={t('admin.ai.key.placeholder')}
            value={apiKeyInput}
            onChange={(e) => {
              setApiKeyInput(e.target.value);
              setSaved(false);
            }}
          />
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={saving || keyInfo.source !== 'db'}
            onClick={() => void handleClearKey()}
          >
            {t('admin.ai.key.clear')}
          </button>
        </div>
        <p className={styles.hint}>{t('admin.ai.key.hint')}</p>
      </section>

      <div className={styles.sources}>
        {SOURCES.map((source) => {
          const outcome = testResults[source];
          return (
            <section key={source} className={styles.sourceCard}>
              <div className={styles.sourceHead}>
                <h3 className={styles.subtitle}>{t(`admin.ai.source.${source}`)}</h3>
                <p className={styles.hint}>{t(`admin.ai.source.${source}.hint`)}</p>
              </div>
              <div className={styles.sourceBody}>
                <ModelPicker
                  id={`ai-model-${source}`}
                  label={t('admin.ai.model')}
                  value={settings.models[source]}
                  onChange={(id) => updateModel(source, id)}
                  models={models}
                  loading={modelsLoading}
                  visionOnly={source === 'screenshot'}
                />

                {/* Чем выбранная модель не годится этой задаче. Стоит сразу
                    под выбором, а не внизу карточки: это ответ на только что
                    сделанный выбор. */}
                {issuesFor(source).map((issue) => (
                  <p key={issue} className={styles.issue} role="alert">
                    {issueMessage(t, issue, source)}
                  </p>
                ))}

                {/* Подборка под задачу: каталог провайдера — четыре сотни
                    моделей, и выбрать из него вслепую нельзя. Подробности
                    выбора — в recommendedModels.ts. */}
                <div className={styles.recommend}>
                  <p className={styles.recommendTitle}>{t('admin.ai.recommend')}</p>
                  <ul className={styles.recommendList}>
                    {RECOMMENDED_MODELS[source].map((rec) => {
                      // Цена и возможности — из живого каталога, когда модель в
                      // нём есть: цифры в подборке снимались один раз и стареют,
                      // а список у провайдера обновляется. Снимок остаётся
                      // запасным вариантом — на случай, если каталог ещё не
                      // загрузился или модель из него пропала.
                      const live = modelById(models, rec.id);
                      return (
                        <li key={rec.id}>
                          <button
                            type="button"
                            className={styles.recommendItem}
                            aria-pressed={settings.models[source] === rec.id}
                            onClick={() => updateModel(source, rec.id)}
                          >
                            <span className={styles.recommendId}>{rec.id}</span>
                            <span className={styles.recommendPrice}>
                              {live
                                ? formatPrice(live, t)
                                : t('admin.ai.model.price', { in: rec.in, out: rec.out })}
                            </span>
                            {live && (
                              <span className={styles.recommendBadges}>
                                {live.vision && <span>{t('admin.ai.model.vision')}</span>}
                                {live.tools && <span>{t('admin.ai.model.tools')}</span>}
                                {live.reasoning && <span>{t('admin.ai.model.reasoning')}</span>}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <p className={styles.hint}>{t('admin.ai.recommend.hint', { date: RECOMMENDED_AS_OF })}</p>
                </div>

                {/* Свои лимиты задачи — свёрнуты: у большинства задач они
                    наследуются, и разворачивать их каждому незачем. Открыты
                    сразу, если что-то уже переопределено, — иначе настройка
                    была бы спрятана от того, кто её сам и задал. */}
                <details className={styles.tune} open={Boolean(settings.overrides[source])}>
                  <summary className={styles.tuneSummary}>{t('admin.ai.source.settings')}</summary>
                  <div className={styles.tuneGrid}>
                    {TUNING_FIELDS.map((field) => (
                      <TuneField
                        key={field}
                        id={`ai-${source}-${field}`}
                        label={t(fieldLabelKey(field))}
                        value={settings.overrides[source]?.[field]}
                        inherited={settings[field]}
                        bounds={BOUNDS[field]}
                        step={field === 'temperature' ? 0.1 : 1}
                        placeholder={t('admin.ai.override.inherit', { value: settings[field] })}
                        error={overrideErrors[source]?.[field]}
                        onChange={(v) => updateOverride(source, field, v)}
                      />
                    ))}
                  </div>
                  {/* Рекомендация рядом с полями, а не в справке внизу: её
                      читают ровно тогда, когда открыли эти поля и не знают,
                      что вписать. Кнопка проставляет всё разом. */}
                  <div className={styles.tuneRecommend}>
                    <span className={styles.hint}>
                      {t('admin.ai.tune.recommend', { values: formatTuning(t, source) })}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => applyRecommendedTuning(source)}
                    >
                      {t('admin.ai.tune.apply')}
                    </button>
                  </div>
                  <p className={styles.hint}>{t('admin.ai.source.settings.hint')}</p>
                </details>
              </div>
              <div className={styles.testRow}>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={Boolean(testing[source]) || !settings.enabled}
                  onClick={() => void handleTest(source)}
                >
                  {testing[source] ? t('admin.ai.test.running') : t('admin.ai.test')}
                </button>
                {outcome?.ok && <span className={styles.testOk}>{t('admin.ai.test.ok', { ms: outcome.ms })}</span>}
                {outcome && !outcome.ok && (
                  <span className={styles.testFailed}>
                    {t('admin.ai.test.failed', { message: outcome.message })}
                  </span>
                )}
              </div>
            </section>
          );
        })}
      </div>

      <div className={styles.modelsBar}>
        <button
          type="button"
          className="btn btn-sm"
          disabled={modelsLoading}
          onClick={() => void loadModels(true)}
        >
          {t('admin.ai.models.reload')}
        </button>
        {modelsLoading && <span className={styles.hint}>{t('admin.ai.models.loading')}</span>}
        {!modelsLoading && modelsError && (
          <span className={styles.error} role="alert">
            {t('admin.ai.models.failed', { message: apiErrorMessage(t, modelsError) })}
          </span>
        )}
        {!modelsLoading && !modelsError && (
          <span className={styles.hint}>{t('admin.ai.models.count', { count: models.length })}</span>
        )}
      </div>

      {/* Требования к модели магии — внизу, во всю ширину и свёрнутые. Внутри
          карточки этот список растягивал её на пол-экрана и отодвигал вниз всё
          остальное, хотя читают его один раз — когда выбирают модель. */}
      <details className={styles.reqBlock}>
        <summary className={styles.reqSummary}>{t('admin.ai.req.title')}</summary>
        <ul className={styles.reqList}>
          {MAGIC_REQUIREMENTS.map((key) => (
            <li key={key}>{t(key)}</li>
          ))}
        </ul>
        <p className={styles.hint}>{t('admin.ai.req.recommend')}</p>
      </details>

      <section className={styles.advanced}>
        <button
          type="button"
          className="btn btn-sm"
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen((v) => !v)}
        >
          {t('admin.ai.advanced')}
        </button>
        {advancedOpen && (
          <>
            <p className={styles.hint}>{t('admin.ai.advanced.hint')}</p>
            <div className={styles.advancedGrid}>
            <NumField
              id="ai-maxTokens"
              label={t('admin.ai.maxTokens')}
              value={settings.maxTokens}
              bounds={BOUNDS.maxTokens}
              error={rangeErrors.maxTokens}
              onChange={(v) => update({ maxTokens: v })}
            />
            <NumField
              id="ai-temperature"
              label={t('admin.ai.temperature')}
              value={settings.temperature}
              bounds={BOUNDS.temperature}
              step={0.1}
              error={rangeErrors.temperature}
              onChange={(v) => update({ temperature: v })}
            />
            <NumField
              id="ai-timeoutSec"
              label={t('admin.ai.timeout')}
              value={settings.timeoutSec}
              bounds={BOUNDS.timeoutSec}
              error={rangeErrors.timeoutSec}
              onChange={(v) => update({ timeoutSec: v })}
            />
            <NumField
              id="ai-dailyLimit"
              label={t('admin.ai.limit')}
              value={settings.dailyLimit}
              bounds={BOUNDS.dailyLimit}
              error={rangeErrors.dailyLimit}
              onChange={(v) => update({ dailyLimit: v })}
            />
            <NumField
              id="ai-maxImages"
              label={t('admin.ai.maxImages')}
              value={settings.maxImages}
              bounds={BOUNDS.maxImages}
              error={rangeErrors.maxImages}
              onChange={(v) => update({ maxImages: v })}
            />
            </div>
            <p className={styles.hint}>{t('admin.ai.maxImages.hint')}</p>
          </>
        )}
      </section>

      <div className={styles.saveBar}>
        <button type="button" className="btn btn-primary" disabled={!canSave} onClick={() => void handleSave()}>
          {t('admin.ai.save')}
        </button>
        {saved && <span className={styles.testOk}>{t('admin.ai.saved')}</span>}
        {saveError && (
          <span className={styles.error} role="alert">
            {t('admin.ai.save.failed', { message: apiErrorMessage(t, saveError) })}
          </span>
        )}
        <span className={styles.updated}>{t('admin.ai.updated', { date: formatDate(updatedAt) })}</span>
      </div>
    </div>
  );
}

interface NumFieldProps {
  id: string;
  label: string;
  value: number;
  bounds: { min: number; max: number };
  step?: number;
  error?: string;
  onChange(v: number): void;
}

interface TuneFieldProps {
  id: string;
  label: string;
  /** undefined — переопределения нет, действует общее значение. */
  value: number | undefined;
  inherited: number;
  bounds: { min: number; max: number };
  step?: number;
  placeholder: string;
  error?: string;
  onChange(v: number | undefined): void;
}

/**
 * Поле переопределения: пустое — значит «как общее», и в подсказке видно,
 * какое именно. Отдельный компонент от NumField, потому что тому пустое
 * значение запрещено — там всегда есть число.
 */
function TuneField({
  id,
  label,
  value,
  inherited,
  bounds,
  step,
  placeholder,
  error,
  onChange,
}: TuneFieldProps): ReactElement {
  return (
    <div className={styles.field}>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        className="input"
        type="number"
        min={bounds.min}
        max={bounds.max}
        step={step ?? 1}
        value={value ?? ''}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => onChange(Number.isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
      />
      {error ? (
        <p id={`${id}-error`} className="field-error">
          {error}
        </p>
      ) : (
        <p className={styles.inherited}>{value === undefined ? placeholder : `≠ ${inherited}`}</p>
      )}
    </div>
  );
}

function NumField({ id, label, value, bounds, step, error, onChange }: NumFieldProps): ReactElement {
  return (
    <div className={styles.field}>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        className="input"
        type="number"
        min={bounds.min}
        max={bounds.max}
        step={step ?? 1}
        value={Number.isFinite(value) ? value : ''}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(e) => onChange(e.target.valueAsNumber)}
      />
      {error && (
        <p id={`${id}-error`} className="field-error">
          {error}
        </p>
      )}
    </div>
  );
}
